// src/layout/builders/ERP/ERPTrackingTab.jsx
// Project-level ERP Tracking: Finance | Attendance | Petty Cash | Materials
import React, { useEffect, useState, useCallback } from "react";
import useAuthStore from "../../../store/useAuthStore";
import useERPStore from "../../../store/builders/useERPStore";
import useProjectStatus from "../../../store/builders/useProjectStatus";
import { toast } from "react-hot-toast";
import { axiosInstancev1 } from "../../../lib/axiosInstance";
import {
  TrendingUp, TrendingDown, IndianRupee, Users, Wallet, Package, RefreshCw,
  Plus, Trash2, X, ChevronLeft, ChevronRight, ChevronDown, Save, Clock, CheckCircle, XCircle, ArrowRight, Truck,
  HardHat, RotateCcw, CalendarDays, DollarSign,
} from "lucide-react";

const fmt = (n) => `₹${parseFloat(n || 0).toLocaleString("en-IN")}`;
const fmtDate = (d) => d ? d.split("T")[0] : "—";

const SUB_TABS = [
  { key: "finance", label: "Finance", icon: IndianRupee },
  { key: "attendance", label: "Attendance", icon: Users },
  { key: "pettycash", label: "Petty Cash", icon: Wallet },
  { key: "materials", label: "Materials", icon: Package },
];

const STATUS_META = {
  present: { label: "Present", letter: "P", active: "bg-emerald-500 text-white border-emerald-500 ring-emerald-200", idle: "bg-white text-emerald-600 border-emerald-200 hover:bg-emerald-50" },
  half_day: { label: "Half Day", letter: "H", active: "bg-amber-400 text-white border-amber-400 ring-amber-200", idle: "bg-white text-amber-600 border-amber-200 hover:bg-amber-50" },
  absent: { label: "Absent", letter: "A", active: "bg-red-500 text-white border-red-500 ring-red-200", idle: "bg-white text-red-600 border-red-200 hover:bg-red-50" },
};

const normalizeAttendanceStatus = (status) => (status === "leave" ? "absent" : status);
const DAY_NAMES = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const dayEarning = (status, dailySalary) => {
  const rate = parseFloat(dailySalary || 0);
  if (status === "present") return rate;
  if (status === "half_day") return rate * 0.5;
  return 0;
};

const toLocalDateStr = (dateInput) => {
  if (!dateInput) return null;
  const d = new Date(dateInput);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const todayLocalStr = () => toLocalDateStr(new Date());

const CircleStatusPicker = ({ value, onChange, disabled }) => (
  <div className={`inline-flex items-center gap-2 ${disabled ? "opacity-60 pointer-events-none" : ""}`}>
    {Object.entries(STATUS_META).map(([key, meta]) => {
      const selected = value === key;
      return (
        <button
          key={key}
          type="button"
          disabled={disabled}
          title={meta.label}
          onClick={() => onChange(key)}
          className={`w-9 h-9 rounded-full border-2 font-bold text-xs flex items-center justify-center transition-all ${
            selected ? `${meta.active} ring-2 scale-105 shadow-sm` : meta.idle
          }`}
        >
          {meta.letter}
        </button>
      );
    })}
  </div>
);

const EmployeeProjectDetailModal = ({ employee, projectId, builderId, dateFrom, dateTo, onClose, onPaymentDone }) => {
  const [records, setRecords] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [showAdvanceForm, setShowAdvanceForm] = useState(false);
  const [advanceForm, setAdvanceForm] = useState({ amount: "", note: "", advance_date: todayLocalStr() });
  const [savingAdvance, setSavingAdvance] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      let qs = `/erp/attendance/project/${projectId}?builderId=${builderId}`;
      if (dateFrom) qs += `&dateFrom=${dateFrom}`;
      if (dateTo) qs += `&dateTo=${dateTo}`;
      const [attRes, advRes] = await Promise.all([
        axiosInstancev1.get(qs),
        axiosInstancev1.get(`/erp/advances/employee/${employee.employee_id}/project/${projectId}?builderId=${builderId}`),
      ]);
      const rows = (attRes.data?.data || []).filter(
        (r) => String(r.employee_id) === String(employee.employee_id)
      );
      setRecords(rows.sort((a, b) => new Date(b.attendance_date) - new Date(a.attendance_date)));
      setAdvances(advRes.data?.data || []);
    } catch (e) {
      console.error("[Employee attendance detail]", e);
    }
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, [employee.employee_id, projectId, builderId, dateFrom, dateTo]);

  const counts = records.reduce(
    (acc, r) => {
      const status = normalizeAttendanceStatus(r.status);
      if (status === "present") acc.present += 1;
      else if (status === "half_day") acc.half += 0.5;
      else if (status === "absent") acc.absent += 1;
      return acc;
    },
    { present: 0, half: 0, absent: 0 }
  );

  const totalEarned = records.reduce((sum, r) => sum + dayEarning(r.status, employee.daily_salary), 0);
  const totalPaid = records.reduce((sum, r) => r.payment_status === "paid" ? sum + dayEarning(r.status, employee.daily_salary) : sum, 0);
  const totalUnpaidEarned = totalEarned - totalPaid;
  const totalAdvance = advances.reduce((sum, a) => sum + parseFloat(a.amount || 0), 0);
  // Balance = unpaid earnings - advance given (advance already paid out to employee)
  const balanceDue = Math.max(0, totalUnpaidEarned - totalAdvance);
  const advanceCoverage = Math.min(totalAdvance, totalUnpaidEarned);
  const unpaidRecords = records.filter(r => r.payment_status !== "paid" && dayEarning(r.status, employee.daily_salary) > 0);

  const fmtD = (d) =>
    new Date(`${String(d).split("T")[0]}T12:00:00`).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });

  const handlePayAll = async () => {
    if (unpaidRecords.length === 0) return;
    setPaying(true);
    try {
      await Promise.all(
        unpaidRecords.map(r =>
          axiosInstancev1.patch(`/erp/attendance/${r.id}/pay`, {
            paid_amount: dayEarning(r.status, employee.daily_salary),
          })
        )
      );
      toast.success("Payment recorded successfully!");
      await loadAll();
      if (onPaymentDone) onPaymentDone();
    } catch (e) {
      toast.error(e.response?.data?.message || "Payment failed");
    }
    setPaying(false);
  };

  const handleAdvanceSave = async (e) => {
    e.preventDefault();
    const amt = parseFloat(advanceForm.amount);
    if (isNaN(amt) || amt <= 0) { toast.error("Enter a valid advance amount"); return; }
    setSavingAdvance(true);
    try {
      await axiosInstancev1.post("/erp/advances", {
        employee_id: employee.employee_id,
        project_id: projectId,
        builder_id: builderId,
        amount: amt,
        note: advanceForm.note || null,
        advance_date: advanceForm.advance_date,
      });
      toast.success(`Advance of ${fmt(amt)} recorded!`);
      setAdvanceForm({ amount: "", note: "", advance_date: todayLocalStr() });
      setShowAdvanceForm(false);
      await loadAll();
      if (onPaymentDone) onPaymentDone();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to record advance");
    }
    setSavingAdvance(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[70] p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg border border-gray-200 overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-blue-50 to-white">
          <div>
            <h3 className="font-bold text-gray-800 text-sm">{employee.name}</h3>
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">{employee.position || "Staff"} · ₹{parseFloat(employee.daily_salary || 0).toLocaleString()}/day</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100">
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center">
            <div className="w-8 h-8 border-4 border-blue-600/20 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="p-4 grid grid-cols-3 gap-2 border-b border-gray-100">
              {[
                { key: "present", val: counts.present, label: "Present" },
                { key: "half_day", val: counts.half, label: "Half Day" },
                { key: "absent", val: counts.absent, label: "Absent" },
              ].map(({ key, val, label }) => (
                <div key={key} className="bg-gray-50 border border-gray-200 rounded-lg p-2 text-center">
                  <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center text-xs font-bold border-2 ${STATUS_META[key].active}`}>
                    {STATUS_META[key].letter}
                  </span>
                  <p className="text-lg font-black text-gray-800 mt-1">{val}</p>
                  <p className="text-[9px] font-bold text-gray-500 uppercase">{label}</p>
                </div>
              ))}
            </div>

            {/* Financial summary */}
            <div className={`px-4 py-3 grid ${totalAdvance > 0 ? "grid-cols-3" : "grid-cols-2"} gap-2 border-b border-gray-100`}>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5">
                <p className="text-[9px] font-bold text-emerald-500 uppercase tracking-wider">Total Earned</p>
                <p className="text-base font-black text-emerald-700 mt-0.5">{fmt(totalEarned)}</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                <p className="text-[9px] font-bold text-blue-500 uppercase tracking-wider">Paid</p>
                <p className="text-base font-black text-blue-700 mt-0.5">{fmt(totalPaid)}</p>
              </div>
              {totalAdvance > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                  <p className="text-[9px] font-bold text-amber-500 uppercase tracking-wider">Advance Given</p>
                  <p className="text-base font-black text-amber-700 mt-0.5">{fmt(totalAdvance)}</p>
                </div>
              )}
              <div className={`border rounded-lg p-2.5 ${balanceDue > 0 ? "bg-red-50 border-red-200" : (balanceDue < 0 ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-200")} ${totalAdvance > 0 ? "col-span-full" : "col-span-2"}`}>
                <p className={`text-[9px] font-bold uppercase tracking-wider ${balanceDue > 0 ? "text-red-500" : (balanceDue < 0 ? "text-blue-500" : "text-gray-400")}`}>{balanceDue < 0 ? "Advance Remaining" : "Balance Due"}</p>
                <p className={`text-base font-black mt-0.5 ${balanceDue > 0 ? "text-red-700" : (balanceDue < 0 ? "text-blue-700" : "text-gray-400")}`}>{balanceDue !== 0 ? fmt(Math.abs(balanceDue)) : "Cleared"}</p>
              </div>
              {totalAdvance > 0 && totalUnpaidEarned < totalAdvance && (
                <div className="col-span-full bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 text-[10px] text-amber-700 font-semibold">
                  ⚡ Advance covers earnings — employee needs to earn {fmt(totalAdvance - totalUnpaidEarned)} more before payment is due
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="px-4 py-3 flex gap-2 border-b border-gray-100">
              <button
                onClick={handlePayAll}
                disabled={paying || unpaidRecords.length === 0}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl font-bold text-sm hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
              >
                {paying ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Paying...</>
                ) : (
                  <><IndianRupee size={15} /> Pay Unpaid Attendance</>
                )}
              </button>
              <button
                onClick={() => {
                  setShowAdvanceForm(v => {
                    const next = !v;
                    if (next) {
                      setTimeout(() => {
                        const el = document.getElementById("advance-amount-input");
                        if (el) { el.focus(); el.scrollIntoView({ behavior: "smooth", block: "center" }); }
                      }, 50);
                    }
                    return next;
                  });
                }}
                className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm border-2 transition-all ${showAdvanceForm ? "bg-amber-500 text-white border-amber-500" : "bg-white text-amber-600 border-amber-300 hover:bg-amber-50"}`}
              >
                <Wallet size={15} /> Advance
              </button>
            </div>

            {/* Advance form */}
            {showAdvanceForm && (
              <form onSubmit={handleAdvanceSave} className="px-4 py-3 bg-amber-50/60 border-b border-amber-100 space-y-3">
                <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider">Give Advance Payment</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Amount *</label>
                    <input
                      id="advance-amount-input"
                      required
                      type="number"
                      min="1"
                      step="any"
                      placeholder="0"
                      value={advanceForm.amount}
                      onChange={e => setAdvanceForm(f => ({ ...f, amount: e.target.value }))}
                      className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-400 bg-white font-bold text-amber-700"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Date *</label>
                    <input
                      required
                      type="date"
                      value={advanceForm.advance_date}
                      onChange={e => setAdvanceForm(f => ({ ...f, advance_date: e.target.value }))}
                      className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-400 bg-white"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Note (optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. Festival advance"
                    value={advanceForm.note}
                    onChange={e => setAdvanceForm(f => ({ ...f, note: e.target.value }))}
                    className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-400 bg-white"
                  />
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setShowAdvanceForm(false)} className="flex-1 px-3 py-2 border border-gray-200 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-50">Cancel</button>
                  <button type="submit" disabled={savingAdvance} className="flex-1 px-3 py-2 bg-amber-500 text-white rounded-lg text-xs font-bold hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-1">
                    {savingAdvance ? "Saving..." : <><Wallet size={12} /> Record Advance</>}
                  </button>
                </div>
              </form>
            )}

            {/* Advances history */}
            {advances.length > 0 && (
              <div className="px-4 py-3 border-b border-gray-100 bg-amber-50/30">
                <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider mb-2">Advance History</p>
                <div className="space-y-1 max-h-28 overflow-y-auto">
                  {advances.map(a => (
                    <div key={a.id} className="flex items-center justify-between text-xs py-1">
                      <span className="text-gray-500">{fmtD(a.advance_date)}{a.note ? ` — ${a.note}` : ""}</span>
                      <span className="font-bold text-amber-700">{fmt(a.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Attendance table */}
            <div className="flex-1 overflow-y-auto">
              {records.length === 0 ? (
                <p className="py-12 text-center text-sm text-gray-400">No attendance recorded for this project yet.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b sticky top-0">
                    <tr className="text-[10px] font-bold text-gray-500 uppercase">
                      <th className="text-left px-4 py-2">Date</th>
                      <th className="text-center px-2 py-2">Duty</th>
                      <th className="text-right px-4 py-2">Earned</th>
                      <th className="text-center px-2 py-2">Payment</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {records.map((r) => {
                      const status = normalizeAttendanceStatus(r.status);
                      const meta = STATUS_META[status] || STATUS_META.absent;
                      const earned = dayEarning(status, employee.daily_salary);
                      const isPaid = r.payment_status === "paid";
                      return (
                        <tr key={r.id || `${r.attendance_date}-${r.status}`} className="hover:bg-gray-50">
                          <td className="px-4 py-2.5 font-semibold text-gray-700">{fmtD(r.attendance_date)}</td>
                          <td className="px-2 py-2.5 text-center">
                            <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center text-[10px] font-bold border-2 ${meta.active}`}>
                              {meta.letter}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right font-bold text-gray-800">
                            {earned > 0 ? fmt(earned) : "—"}
                          </td>
                          <td className="px-2 py-2.5 text-center">
                            {earned > 0 ? (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-[9px] font-bold uppercase ${isPaid ? "bg-emerald-100 text-emerald-700" : "bg-amber-50 text-amber-600 border border-amber-200"}`}>
                                {isPaid ? "Paid" : "Unpaid"}
                              </span>
                            ) : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};


const SummaryCard = ({ label, value, sub, color = "blue" }) => {
  const COLORS = {
    blue: "bg-blue-50 border-blue-200 text-blue-800 text-blue-600",
    red: "bg-red-50 border-red-200 text-red-800 text-red-600",
    green: "bg-green-50 border-green-200 text-green-800 text-green-600",
    orange: "bg-orange-50 border-orange-200 text-orange-800 text-orange-600",
    purple: "bg-purple-50 border-purple-200 text-purple-800 text-purple-600",
  };
  const [bg, border, textVal, textLabel] = COLORS[color].split(" ");
  return (
    <div className={`${bg} border ${border} rounded-xl p-4`}>
      <p className={`text-xs font-medium ${textLabel}`}>{label}</p>
      <p className={`text-xl font-bold ${textVal} mt-1`}>{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
};

// ─── Finance Sub-tab ──────────────────────────────────────────────────────────
const FinanceSubTab = ({ projectId, builderId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstancev1.get(`/erp/finance/project/${projectId}?builderId=${builderId}`);
      setData(res.data?.data || null);
    } catch (e) { }
    setLoading(false);
  }, [projectId, builderId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-500"></div></div>;
  if (!data) return <div className="text-center py-8 text-gray-400">No finance data available</div>;

  const { project, vendor, salary, petty_cash, summary, purchases } = data;
  const isProfit = summary.net_profit >= 0;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-800"></h3>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <SummaryCard label="Project Value" value={fmt(summary.project_budget)} color="blue" sub={project.status} />
        <SummaryCard label="Vendor Expenses" value={fmt(vendor.total_paid)} color="orange" sub={`Balance: ${fmt(vendor.total_balance)}`} />
        <SummaryCard label="Salary Expenses" value={fmt(salary.total)} color="purple" />
        <SummaryCard label="Petty Cash Used" value={fmt(petty_cash.total_used)} color="red" sub={`Assigned: ${fmt(petty_cash.assigned)}`} />
        <SummaryCard label="Total Expenses" value={fmt(summary.total_expenses)} color="red" />
        <div className={`${isProfit ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"} border rounded-xl p-4`}>
          <p className="text-xs font-medium text-gray-600">Net Profit / Loss</p>
          <div className="flex items-center gap-1.5 mt-1">
            {isProfit ? <TrendingUp size={18} className="text-green-600" /> : <TrendingDown size={18} className="text-red-600" />}
            <p className={`text-xl font-bold ${isProfit ? "text-green-700" : "text-red-700"}`}>
              {isProfit ? "+" : ""}{fmt(summary.net_profit)}
            </p>
          </div>
        </div>
      </div>

      {/* Expense Breakdown Bar */}
      {summary.total_expenses > 0 && (
        <div className="bg-white rounded-xl border p-4">
          <p className="text-xs font-semibold text-gray-600 mb-3">Expense Breakdown</p>
          <div className="flex h-4 rounded-full overflow-hidden">
            <div className="bg-orange-400 transition-all" style={{ width: `${(vendor.total_paid / summary.total_expenses) * 100}%` }} title={`Vendor: ${fmt(vendor.total_paid)}`}></div>
            <div className="bg-purple-400 transition-all" style={{ width: `${(salary.total / summary.total_expenses) * 100}%` }} title={`Salary: ${fmt(salary.total)}`}></div>
            <div className="bg-red-400 transition-all" style={{ width: `${(petty_cash.total_used / summary.total_expenses) * 100}%` }} title={`Petty: ${fmt(petty_cash.total_used)}`}></div>
          </div>
          <div className="flex gap-4 mt-2 text-xs text-gray-500">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-orange-400 rounded-full inline-block"></span>Vendor</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-purple-400 rounded-full inline-block"></span>Salary</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-red-400 rounded-full inline-block"></span>Petty Cash</span>
          </div>
        </div>
      )}

      {/* Vendor Purchases Table */}
      {purchases && purchases.length > 0 && (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50">
            <p className="font-semibold text-gray-700 text-sm">Vendor Purchases for this Project</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left px-4 py-2">Vendor</th>
                  <th className="text-left px-4 py-2">Item</th>
                  <th className="text-right px-4 py-2">Qty</th>
                  <th className="text-right px-4 py-2">Total</th>
                  <th className="text-right px-4 py-2">Paid</th>
                  <th className="text-right px-4 py-2">Balance</th>
                  <th className="text-left px-4 py-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map(p => (
                  <tr key={p.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium text-gray-800">{p.vendor_name}</td>
                    <td className="px-4 py-2 text-gray-700">{p.item_name}</td>
                    <td className="px-4 py-2 text-right text-gray-600">{parseFloat(p.quantity).toLocaleString()} {p.unit}</td>
                    <td className="px-4 py-2 text-right font-semibold text-gray-800">{fmt(p.total_amount)}</td>
                    <td className="px-4 py-2 text-right text-green-700">{fmt(p.paid_amount)}</td>
                    <td className={`px-4 py-2 text-right font-semibold ${parseFloat(p.balance_amount) > 0 ? "text-red-600" : "text-gray-500"}`}>{fmt(p.balance_amount)}</td>
                    <td className="px-4 py-2 text-gray-500">{fmtDate(p.purchase_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Attendance Sub-tab ───────────────────────────────────────────────────────
const TYPE_COLOR_ATT = {
  Tile: "bg-amber-50 text-amber-700 border-amber-100",
  Brick: "bg-red-50 text-red-700 border-red-100",
  Plumbing: "bg-blue-50 text-blue-700 border-blue-100",
  Electrical: "bg-yellow-50 text-yellow-700 border-yellow-100",
  Carpentry: "bg-orange-50 text-orange-700 border-orange-100",
  Painting: "bg-pink-50 text-pink-700 border-pink-100",
  Mason: "bg-stone-50 text-stone-700 border-stone-100",
  Other: "bg-gray-50 text-gray-700 border-gray-100",
};

// ── Contractor Attendance (Timeline boxes) ───────────────────────────────────
const ContractorAttendanceSubTab = ({ projectId, builderId }) => {
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstancev1.get(`/erp/contractor-assignments/project/${projectId}?builderId=${builderId}`);
      setContractors(res.data?.data || []);
    } catch (e) { }
    setLoading(false);
  }, [projectId, builderId]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (id, status) => {
    setUpdatingId(id);
    try {
      const completedDate = status === "completed" ? new Date().toISOString().split("T")[0] : null;
      await axiosInstancev1.put(`/erp/contractor-assignments/${id}/status`, {
        builder_id: builderId, status, completed_date: completedDate,
      });
      toast.success(status === "completed" ? "Contract marked complete" : "Revert request sent");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to update status");
    }
    setUpdatingId(null);
  };

  if (loading) return <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500"></div></div>;

  if (contractors.length === 0) return (
    <div className="py-16 text-center bg-gray-50 rounded-xl border border-dashed border-gray-200">
      <HardHat size={32} className="text-gray-200 mx-auto mb-3" />
      <p className="font-semibold text-gray-600 text-sm">No Contractors Assigned</p>
      <p className="text-xs text-gray-400 mt-1">Assign contractors to this project from the ERP Employee tab.</p>
    </div>
  );

  return (
    <div className="space-y-3">
      {contractors.map(c => {
        const status = c.contract_status || "active";
        const startDate = c.start_date ? new Date(c.start_date) : null;
        const endDate = c.end_date ? new Date(c.end_date) : null;
        const completedDate = c.completed_date ? new Date(c.completed_date) : null;
        const isUpdating = updatingId === c.id;

        const fmtD = (d) => d ? d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

        return (
          <div key={c.id} className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-all ${
            status === "completed" ? "border-blue-200 bg-blue-50/30" :
            status === "revert_requested" ? "border-amber-200 bg-amber-50/20" :
            "border-gray-200"
          }`}>
            <div className="p-4">
              {/* Header */}
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm border-2 flex-shrink-0 ${
                    status === "completed" ? "bg-blue-100 text-blue-700 border-blue-200" :
                    status === "revert_requested" ? "bg-amber-100 text-amber-700 border-amber-200" :
                    "bg-orange-100 text-orange-600 border-orange-200"
                  }`}>
                    {(c.name || "C").charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-gray-800 text-sm">{c.name}</p>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded border font-bold uppercase ${TYPE_COLOR_ATT[c.contractor_type] || "bg-gray-50 text-gray-700 border-gray-100"}`}>
                        {c.contractor_type}
                      </span>
                      {status === "active" && <span className="text-[9px] px-1.5 py-0.5 rounded border font-bold uppercase bg-emerald-50 text-emerald-700 border-emerald-200">Active</span>}
                      {status === "completed" && <span className="text-[9px] px-1.5 py-0.5 rounded border font-bold uppercase bg-blue-50 text-blue-700 border-blue-200">Completed</span>}
                      {status === "revert_requested" && <span className="text-[9px] px-1.5 py-0.5 rounded border font-bold uppercase bg-amber-50 text-amber-700 border-amber-200">Revert Requested</span>}
                    </div>
                    <p className="text-[10px] text-gray-400 font-medium mt-0.5">
                      {c.company_name || (() => {
                        const w = (c.name || "").trim().split(/\s+/);
                        return w.length >= 2 ? w.slice(1).map(x => x[0].toUpperCase()).join("") : (c.name || "").slice(0, 2).toUpperCase();
                      })()}
                    </p>
                  </div>
                </div>
                {c.contract_amount && (
                  <div className="bg-orange-50 border border-orange-100 rounded-lg px-3 py-1.5 text-right">
                    <p className="text-[9px] font-bold text-orange-400 uppercase tracking-wider">Contract Value</p>
                    <p className="text-sm font-black text-orange-700">{fmt(c.contract_amount)}</p>
                  </div>
                )}
              </div>

              {/* Timeline */}
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <CalendarDays size={12} className="text-gray-400" />
                    <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Timeline</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="bg-emerald-50 border border-emerald-100 px-2 py-1 rounded text-xs font-bold text-emerald-700">
                      {startDate ? fmtD(startDate) : "Open start"}
                    </div>
                    <ArrowRight size={12} className="text-gray-400" />
                    {status === "completed" && completedDate ? (
                      <div className="bg-blue-50 border border-blue-200 px-2 py-1 rounded text-xs font-bold text-blue-700 flex items-center gap-1">
                        <CheckCircle size={10} /> Completed {fmtD(completedDate)}
                      </div>
                    ) : (
                      <div className={`px-2 py-1 rounded text-xs font-bold ${endDate ? "bg-gray-100 border border-gray-200 text-gray-600" : "bg-gray-50 border border-dashed border-gray-200 text-gray-400"}`}>
                        {endDate ? fmtD(endDate) : "Ongoing"}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 flex-wrap">
                {status === "active" && (
                  <button
                    onClick={() => handleStatusChange(c.id, "completed")}
                    disabled={isUpdating}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 transition shadow-sm disabled:opacity-50"
                  >
                    <CheckCircle size={12} /> Mark Complete
                  </button>
                )}
                {status === "completed" && (
                  <button
                    onClick={() => handleStatusChange(c.id, "revert_requested")}
                    disabled={isUpdating}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-bold hover:bg-amber-600 transition shadow-sm disabled:opacity-50"
                  >
                    <RotateCcw size={12} /> Request Revert
                  </button>
                )}
                {status === "revert_requested" && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-xs font-bold text-amber-700">
                    <Clock size={12} /> Revert request pending approval
                  </div>
                )}
                {isUpdating && (
                  <div className="flex items-center gap-2 text-xs text-gray-500 font-medium">
                    <div className="w-3 h-3 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                    Updating...
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Employee Attendance (daily marking) ──────────────────────────────────────
const EmployeeAttendanceSubTab = ({ projectId, builderId }) => {
  const [selectedDate, setSelectedDate] = useState(todayLocalStr());
  const [projectStartDate, setProjectStartDate] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [matrixData, setMatrixData] = useState({});
  const [pendingChanges, setPendingChanges] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState([]);
  const [conflictData, setConflictData] = useState({});
  const [detailEmployee, setDetailEmployee] = useState(null);

  const maxDate = todayLocalStr();
  const canGoPrev = !projectStartDate || selectedDate > projectStartDate;
  const canGoNext = selectedDate < maxDate;

  useEffect(() => {
    if (!projectId) return;
    const loadProjectStart = async () => {
      try {
        const res = await axiosInstancev1.get(`/project-master/${projectId}`);
        const starting = res.data?.data?.project?.starting_date;
        const startStr = toLocalDateStr(starting);
        if (startStr) {
          setProjectStartDate(startStr);
          setSelectedDate((prev) => {
            const today = todayLocalStr();
            if (prev > today) return today;
            if (prev < startStr) return startStr;
            return prev;
          });
        }
      } catch (e) {
        console.error("[Attendance] project start date", e);
      }
    };
    loadProjectStart();
  }, [projectId]);

  useEffect(() => { if (projectId && builderId) loadData(); }, [selectedDate, projectId, builderId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [assignRes, attRes, summRes] = await Promise.all([
        axiosInstancev1.get(`/erp/assignments/project/${projectId}?builderId=${builderId}`),
        axiosInstancev1.get(`/erp/attendance/project/${projectId}?builderId=${builderId}&dateFrom=${selectedDate}&dateTo=${selectedDate}`),
        axiosInstancev1.get(`/erp/attendance/summary/${projectId}?builderId=${builderId}`),
      ]);
      const allWorkers = assignRes.data?.data || [];
      // Only show employees whose assignment covers the selected date
      const empOnly = allWorkers.filter(e => {
        if (e.worker_type === "contractor") return false;
        const start = e.start_date ? e.start_date.split("T")[0] : null;
        const end = e.end_date ? e.end_date.split("T")[0] : null;
        if (start && selectedDate < start) return false;
        if (end && selectedDate > end) return false;
        return true;
      });
      const attRecords = attRes.data?.data || [];
      const globalSummary = summRes.data?.data || [];
      setEmployees(empOnly);
      setSummary(globalSummary);

      if (empOnly.length > 0) {
        const empIds = empOnly.filter(e => e.employee_id).map(e => e.employee_id).join(",");
        const globRes = await axiosInstancev1.get(`/erp/attendance/global?builderId=${builderId}&employeeIds=${empIds}&dateFrom=${selectedDate}&dateTo=${selectedDate}`);
        const conflicts = {};
        (globRes.data?.data || []).forEach(a => {
          if (String(a.project_id) !== String(projectId) && (a.status === "present" || a.status === "half_day")) {
            conflicts[a.employee_id] = a.project_name;
          }
        });
        setConflictData(conflicts);
      }

      const matrix = {};
      empOnly.forEach(e => { matrix[e.employee_id] = { status: "", payment_status: "unpaid" }; });
      attRecords.forEach(a => { if (a.employee_id) matrix[a.employee_id] = { status: a.status, payment_status: a.payment_status }; });
      setMatrixData(matrix);
      setPendingChanges({});
    } catch (e) { console.error("[Attendance]", e); }
    setLoading(false);
  };

  const setAttendanceStatus = (empId, status) => {
    if (conflictData[empId]) { toast.error(`Busy at "${conflictData[empId]}" today.`); return; }
    const base = matrixData[empId] ?? { status: "", payment_status: "unpaid" };
    if (status === base.status) {
      setPendingChanges((prev) => {
        const next = { ...prev };
        delete next[empId];
        return next;
      });
      return;
    }
    setPendingChanges((prev) => ({ ...prev, [empId]: { ...base, status } }));
  };

  const getCellData = (empId) => pendingChanges.hasOwnProperty(empId) ? pendingChanges[empId] : (matrixData[empId] ?? { status: "", payment_status: "unpaid" });

  const handleDateChange = (e) => {
    const val = e.target.value;
    if (!val) return;
    if (projectStartDate && val < projectStartDate) {
      toast.error("Cannot select a date before the project start date");
      return;
    }
    if (val > maxDate) {
      toast.error("Future dates are not allowed for attendance");
      return;
    }
    setSelectedDate(val);
  };

  const adjDate = (days) => {
    const d = new Date(`${selectedDate}T12:00:00`);
    d.setDate(d.getDate() + days);
    const next = toLocalDateStr(d);
    if (projectStartDate && next < projectStartDate) return;
    if (next > maxDate) return;
    setSelectedDate(next);
  };

  const handleSave = async () => {
    if (Object.keys(pendingChanges).length === 0) return;
    setSaving(true);
    try {
      await Promise.all(
        Object.entries(pendingChanges).map(([empId, data]) =>
          axiosInstancev1.post("/erp/attendance", {
            employee_id: empId, project_id: projectId, builder_id: builderId,
            attendance_date: selectedDate, status: data.status, payment_status: data.payment_status,
          })
        )
      );
      const newMatrix = { ...matrixData, ...pendingChanges };
      setMatrixData(newMatrix);
      setPendingChanges({});
      toast.success("Attendance saved");
      loadData();
    } catch (e) {
      toast.error(e.response?.status === 409 ? e.response.data.message : "Failed to save attendance.");
    }
    setSaving(false);
  };

  const hasPending = Object.keys(pendingChanges).length > 0;

  const fmtPickerDate = (dateStr) =>
    dateStr
      ? new Date(`${dateStr}T12:00:00`).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
      : "—";

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 bg-gray-50 p-1.5 rounded-lg border border-gray-200">
              <button
                type="button"
                onClick={() => adjDate(-1)}
                disabled={!canGoPrev}
                className="p-1.5 bg-white border border-gray-200 rounded-md text-gray-600 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 enabled:hover:text-blue-600"
                aria-label="Previous day"
              >
                <ChevronLeft size={16} />
              </button>
              <div className="flex flex-col px-1 min-w-[140px]">
                <input
                  type="date"
                  value={selectedDate}
                  min={projectStartDate || undefined}
                  max={maxDate}
                  onChange={handleDateChange}
                  className="font-semibold text-gray-700 bg-transparent border-none focus:ring-0 p-0 text-sm cursor-pointer hover:text-blue-600"
                />
                <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider pl-0.5">
                  {new Date(`${selectedDate}T12:00:00`).toLocaleDateString("en-US", { weekday: "short" })}
                </p>
              </div>
              <button
                type="button"
                onClick={() => adjDate(1)}
                disabled={!canGoNext}
                className="p-1.5 bg-white border border-gray-200 rounded-md text-gray-600 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 enabled:hover:text-blue-600"
                aria-label="Next day"
              >
                <ChevronRight size={16} />
              </button>
            </div>
            {projectStartDate && (
              <p className="text-[10px] text-gray-400 pl-1">
                {fmtPickerDate(projectStartDate)} — {fmtPickerDate(maxDate)} (project range)
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadData} className="p-1.5 bg-white border border-gray-200 rounded-lg text-gray-400 hover:text-gray-900" title="Refresh"><RefreshCw size={14} /></button>
            {hasPending && (
              <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 disabled:opacity-50">
                {saving ? "Saving..." : `Save (${Object.keys(pendingChanges).length})`}
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="py-12 flex flex-col items-center justify-center gap-2">
            <div className="w-8 h-8 border-4 border-blue-600/20 border-t-blue-600 rounded-full animate-spin"></div>
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Loading...</p>
          </div>
        ) : employees.length === 0 ? (
          <div className="py-12 text-center bg-gray-50 rounded-xl border border-dashed border-gray-200">
            <Users size={24} className="text-gray-200 mx-auto mb-2" />
            <h4 className="font-semibold text-gray-700 text-sm">No Employees Assigned</h4>
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-lg border border-gray-100 shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase">Workforce</th>
                  <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase text-center">Duty Status</th>
                  <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase text-right">Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {employees.map(emp => {
                  const data = getCellData(emp.employee_id);
                  const isPending = pendingChanges.hasOwnProperty(emp.employee_id);
                  const conflictProject = conflictData[emp.employee_id];
                  return (
                    <tr key={emp.employee_id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs border-2 transition-all ${data.status ? "bg-blue-600 text-white border-blue-100" : "bg-gray-50 text-gray-400 border-gray-100"}`}>
                            {(emp.name || "E").charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <button
                              type="button"
                              onClick={() => setDetailEmployee(emp)}
                              className="font-semibold text-gray-800 text-sm hover:text-blue-600 text-left transition-colors"
                            >
                              {emp.name}
                            </button>
                            <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">{emp.position || "Staff"}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-center">
                          {conflictProject ? (
                            <div className="px-3 py-1.5 bg-red-50 text-red-600 border border-red-100 rounded-lg flex flex-col items-center cursor-not-allowed">
                              <span className="text-[10px] font-bold uppercase">BUSY</span>
                              <span className="text-[8px] font-bold uppercase max-w-[80px] truncate">@{conflictProject}</span>
                            </div>
                          ) : (
                            <div className={isPending ? "ring-2 ring-blue-400/30 rounded-full p-0.5" : ""}>
                              <CircleStatusPicker
                                value={normalizeAttendanceStatus(data.status)}
                                onChange={(status) => setAttendanceStatus(emp.employee_id, status)}
                              />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="text-gray-700 font-semibold text-sm">₹{parseFloat(emp.daily_salary || 0).toLocaleString()}</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-gray-100">
          {Object.entries(STATUS_META).map(([key, meta]) => (
            <div key={key} className="flex items-center gap-2 px-3 py-1 bg-gray-50 rounded-md border border-gray-200">
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 ${meta.active}`}>
                {meta.letter}
              </span>
              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{meta.label}</span>
            </div>
          ))}
        </div>
      </div>

      {summary.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50">
            <p className="font-bold text-gray-700 text-xs uppercase tracking-wider">Workforce Analytics</p>
          </div>
          <div className="max-h-[300px] overflow-y-auto">
            <table className="w-full text-left">
              <thead className="bg-white sticky top-0 z-10">
                <tr className="text-[9px] font-bold uppercase tracking-wider text-gray-400 border-b">
                  <th className="px-4 py-2">Workforce</th>
                  <th className="px-2 py-2 text-center">Duty</th>
                  <th className="px-4 py-2 text-right">Amount to Pay</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {summary.map((s) => {
                  const empRow = employees.find((e) => e.employee_id === s.employee_id);
                  return (
                    <tr key={s.employee_id} className="hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <button
                          type="button"
                          onClick={() => empRow && setDetailEmployee(empRow)}
                          className="font-semibold text-gray-800 text-xs hover:text-blue-600 text-left"
                        >
                          {s.employee_name}
                        </button>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <span className="font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded text-[10px]">
                          {s.present_days}P · {s.half_days}H · {s.absent_days}A
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right font-bold text-gray-800 text-xs">{fmt(s.estimated_salary)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detailEmployee && (
        <EmployeeProjectDetailModal
          employee={detailEmployee}
          projectId={projectId}
          builderId={builderId}
          dateFrom={projectStartDate}
          dateTo={maxDate}
          onClose={() => setDetailEmployee(null)}
          onPaymentDone={() => loadData()}
        />
      )}
    </div>
  );
};

// ── Combined Attendance Sub-tab (with Employee / Contractor inner tabs) ────────
const AttendanceSubTab = ({ projectId, builderId }) => {
  const [innerTab, setInnerTab] = useState("employees");
  return (
    <div className="space-y-4">
      {/* Inner tab switcher */}
      <div className="flex gap-1.5 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setInnerTab("employees")}
          className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-bold transition-all ${innerTab === "employees" ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
        >
          <Users size={13} /> Employees
        </button>
        <button
          onClick={() => setInnerTab("contractors")}
          className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-bold transition-all ${innerTab === "contractors" ? "bg-white text-orange-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
        >
          <HardHat size={13} /> Contractors
        </button>
      </div>
      {innerTab === "employees"
        ? <EmployeeAttendanceSubTab projectId={projectId} builderId={builderId} />
        : <ContractorAttendanceSubTab projectId={projectId} builderId={builderId} />
      }
    </div>
  );
};

// ─── Petty Cash Sub-tab ───────────────────────────────────────────────────────
const PettyCashSubTab = ({ projectId, builderId }) => {
  const [pettyCash, setPettyCash] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [requestForm, setRequestForm] = useState({ purpose: "", amount: "", notes: "" });
  const [expenseForm, setExpenseForm] = useState({ description: "", amount: "", expense_date: new Date().toISOString().split("T")[0] });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pcRes, expRes, reqRes] = await Promise.all([
        axiosInstancev1.get(`/erp/petty-cash/project/${projectId}?builderId=${builderId}`),
        axiosInstancev1.get(`/erp/petty-cash/expenses/project/${projectId}?builderId=${builderId}`),
        axiosInstancev1.get(`/erp/petty-cash/requests?builderId=${builderId}`),
      ]);
      const pcData = (pcRes.data?.data || [])[0] || null;
      setPettyCash(pcData);
      setExpenses(expRes.data?.data || []);
      setRequests((reqRes.data?.data || []).filter(r => String(r.project_id) === String(projectId)));
    } catch (e) { }
    setLoading(false);
  }, [projectId, builderId]);

  useEffect(() => { load(); }, [load]);

  const handleRequestSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await axiosInstancev1.post("/erp/petty-cash/requests", { project_id: projectId, builder_id: builderId, ...requestForm });
      setShowRequestModal(false);
      setRequestForm({ purpose: "", amount: "", notes: "" });
      await load();
    } catch (err) { }
    setSaving(false);
  };

  const handleExpenseSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await axiosInstancev1.post("/erp/petty-cash/expenses", { project_id: projectId, builder_id: builderId, ...expenseForm });
      setShowExpenseModal(false);
      setExpenseForm({ description: "", amount: "", expense_date: new Date().toISOString().split("T")[0] });
      await load();
    } catch (err) { }
    setSaving(false);
  };

  const handleDeleteExpense = async (id) => {
    await axiosInstancev1.delete(`/erp/petty-cash/expenses/${id}?builderId=${builderId}`);
    await load();
  };

  if (loading) return <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-500"></div></div>;

  const assigned = parseFloat(pettyCash?.assigned_amount || 0);
  const used = parseFloat(pettyCash?.used_amount || 0);
  const remaining = parseFloat(pettyCash?.remaining_amount || assigned - used);
  const pendingRequests = requests.filter(r => r.status === "pending");

  return (
    <div className="space-y-4">
      {/* Balance Cards */}
      <div className="grid grid-cols-3 gap-3">
        <SummaryCard label="Assigned" value={fmt(assigned)} color="blue" />
        <SummaryCard label="Used" value={fmt(used)} color="red" />
        <SummaryCard label="Remaining" value={fmt(remaining)} color={remaining >= 0 ? "green" : "red"} />
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setShowExpenseModal(true)} className="flex items-center gap-1.5 px-3 py-2 bg-purple-500 text-white rounded-lg text-sm font-medium hover:bg-purple-600 transition">
          <Plus size={14} /> Log Expense
        </button>
        <button onClick={() => setShowRequestModal(true)} className="flex items-center gap-1.5 px-3 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition">
          <Wallet size={14} /> Request Funds
        </button>
        <button onClick={load} className="flex items-center gap-1 px-3 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition ml-auto">
          <RefreshCw size={13} />
        </button>
      </div>


      {/* All Requests History */}
      {requests.length > 0 && (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50">
            <p className="font-semibold text-gray-700 text-sm">Fund Request History</p>
          </div>
          <div className="divide-y">
            {requests.map(req => {
              const statusCfg = { pending: "bg-yellow-100 text-yellow-700", approved: "bg-green-100 text-green-700", rejected: "bg-red-100 text-red-700" };
              return (
                <div key={req.id} className="px-4 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{req.purpose}</p>
                    <p className="text-xs text-gray-500">{fmt(req.amount)} · {fmtDate(req.created_at)}</p>
                    {req.notes && (
                      <p className="text-xs text-gray-500 mt-1 whitespace-pre-line italic">
                        {req.notes.replace(/\nReason: /g, '')}
                      </p>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${statusCfg[req.status]}`}>{req.status}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Expense Log */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50">
          <p className="font-semibold text-gray-700 text-sm">Expense Log</p>
        </div>
        {expenses.length === 0 ? (
          <div className="p-6 text-center text-gray-400 text-sm">No expenses logged</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="text-left px-4 py-2">Description</th>
                <th className="text-right px-4 py-2">Amount</th>
                <th className="text-left px-4 py-2">Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {expenses.map(exp => (
                <tr key={exp.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-800">{exp.description}</td>
                  <td className="px-4 py-2 text-right font-semibold text-red-600">{fmt(exp.amount)}</td>
                  <td className="px-4 py-2 text-gray-500">{fmtDate(exp.expense_date)}</td>
                  <td className="px-3 py-2"><button onClick={() => handleDeleteExpense(exp.id)} className="p-1 text-gray-300 hover:text-red-500 transition"><Trash2 size={12} /></button></td>
                </tr>
              ))}
              <tr className="border-t bg-gray-50">
                <td className="px-4 py-2 font-semibold text-gray-700">Total</td>
                <td className="px-4 py-2 text-right font-bold text-red-600">{fmt(expenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0))}</td>
                <td colSpan={2}></td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      {/* Request Funds Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="font-bold text-gray-800">Request Funds</h3>
              <button onClick={() => setShowRequestModal(false)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
            </div>
            <form onSubmit={handleRequestSave} className="p-5 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Purpose *</label>
                <input required value={requestForm.purpose} onChange={e => setRequestForm(p => ({ ...p, purpose: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-200" placeholder="e.g. Tea & travel expenses" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Amount (₹) *</label>
                <input required type="number" min="1" value={requestForm.amount} onChange={e => setRequestForm(p => ({ ...p, amount: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-200" placeholder="500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                <input value={requestForm.notes} onChange={e => setRequestForm(p => ({ ...p, notes: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-200" placeholder="Additional notes..." />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowRequestModal(false)} className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-xl text-sm font-medium hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 bg-orange-500 text-white py-2 rounded-xl text-sm font-medium hover:bg-orange-600 disabled:opacity-60">{saving ? "Submitting..." : "Submit"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Log Expense Modal */}
      {showExpenseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="font-bold text-gray-800">Log Expense</h3>
              <button 
                onClick={() => setShowExpenseModal(false)} 
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleExpenseSave} className="p-5 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Description *</label>
                <input 
                  required 
                  value={expenseForm.description} 
                  onChange={e => setExpenseForm(p => ({ ...p, description: e.target.value }))} 
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-200" 
                  placeholder="e.g. Transport or Materials" 
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Amount (₹) *</label>
                <input 
                  required 
                  type="number" 
                  min="1" 
                  value={expenseForm.amount} 
                  onChange={e => setExpenseForm(p => ({ ...p, amount: e.target.value }))} 
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-200" 
                  placeholder="100" 
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Date *</label>
                <input 
                  required 
                  type="date" 
                  value={expenseForm.expense_date} 
                  onChange={e => setExpenseForm(p => ({ ...p, expense_date: e.target.value }))} 
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-200" 
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button 
                  type="button" 
                  onClick={() => setShowExpenseModal(false)} 
                  className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-xl text-sm font-medium hover:bg-gray-50">
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={saving} 
                  className="flex-1 bg-purple-500 text-white py-2 rounded-xl text-sm font-medium hover:bg-purple-600 disabled:opacity-60">
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Materials Sub-tab ────────────────────────────────────────────────────────
const MaterialsSubTab = ({ projectId, builderId }) => {
  const [inventoryStatus, setInventoryStatus] = useState([]);
  const [usageLog, setUsageLog] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [usageForm, setUsageForm] = useState({ quantity_used: "", usage_date: new Date().toISOString().split("T")[0], notes: "" });
  const [transferForm, setTransferForm] = useState({ from_project_id: "", quantity: "", notes: "" });
  const [selectedItem, setSelectedItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [itemAvailability, setItemAvailability] = useState([]);
  const [transferFilter, setTransferFilter] = useState("all"); // all | pending | accepted | rejected
  const [showTransferLog, setShowTransferLog] = useState(true);
  const [showUsageLog, setShowUsageLog] = useState(true);

  const { projects, fetchProjects } = useProjectStatus();
  const { inventory, fetchInventory } = useERPStore();
  const load = useCallback(async () => {
    setLoading(true);
    try {
      fetchProjects();
      fetchInventory();
      const [invRes, usageRes, transRes] = await Promise.all([
        axiosInstancev1.get(`/erp/inventory/project-status/${projectId}?builderId=${builderId}`),
        axiosInstancev1.get(`/erp/material-usage/project/${projectId}?builderId=${builderId}`),
        axiosInstancev1.get(`/erp/material-transfers/project/${projectId}?builderId=${builderId}`),
      ]);
      setInventoryStatus(invRes.data?.data || []);
      setUsageLog(usageRes.data?.data || []);
      setTransfers(transRes.data?.data || []);
    } catch (e) { }
    setLoading(false);
  }, [projectId, builderId]);

  const handleTransferStatusUpdate = async (id, status) => {
    setSaving(true);
    try {
      await axiosInstancev1.put(`/erp/material-transfers/${id}/status`, { status, builder_id: builderId });
      toast.success(`Transfer ${status}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update transfer");
    }
    setSaving(false);
  };

  useEffect(() => { load(); }, [load]);

  const fetchItemAvailability = useCallback(async (itemName) => {
    if (!itemName) return;
    try {
      const res = await axiosInstancev1.get(`/erp/inventory/item-availability?itemName=${encodeURIComponent(itemName)}&builderId=${builderId}`);
      setItemAvailability(res.data?.data || []);
    } catch (e) { }
  }, [builderId]);

  useEffect(() => {
    if (showTransferModal) {
      const itemName = selectedItem?.item_name || transferForm.item_name;
      if (itemName) fetchItemAvailability(itemName);
    } else {
      setItemAvailability([]);
    }
  }, [showTransferModal, selectedItem, transferForm.item_name, fetchItemAvailability]);

  const handleUsageSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await axiosInstancev1.post("/erp/material-usage", {
        project_id: projectId,
        builder_id: builderId,
        inventory_id: selectedItem.id,
        item_name: selectedItem.item_name,
        unit: selectedItem.unit,
        ...usageForm
      });
      setShowUsageModal(false);
      setUsageForm({ quantity_used: "", usage_date: new Date().toISOString().split("T")[0], notes: "" });
      await load();
    } catch (err) { }
    setSaving(false);
  };

  const handleTransferSave = async (e) => {
    e.preventDefault();
    const itemName = selectedItem?.item_name || transferForm.item_name;
    const fromId = transferForm.from_project_id;
    const isGodown = fromId === "" || fromId === "0" || fromId === 0 || fromId == null;
    const availability = itemAvailability.find(a =>
      isGodown ? Number(a.project_id) === 0 : String(a.project_id) === String(fromId)
    );
    const availableStock = parseFloat(availability?.available_stock || 0);
    const requestedQty = parseFloat(transferForm.quantity || 0);

    if (requestedQty > availableStock) {
      toast.error(`Only ${availableStock} ${selectedItem?.unit || transferForm.unit || ''} available in selected source.`);
      return;
    }

    setSaving(true);
    try {
      await axiosInstancev1.post("/erp/material-transfers", {
        to_project_id: projectId,
        builder_id: builderId,
        item_name: itemName,
        unit: selectedItem?.unit || transferForm.unit,
        ...transferForm,
        from_project_id: isGodown ? null : fromId,
      });
      setShowTransferModal(false);
      setTransferForm({ from_project_id: "", quantity: "", notes: "" });
      await load();
      toast.success(isGodown ? "Stock transferred from Godown" : "Transfer request submitted — awaiting Accept/Reject");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to submit request.");
    }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-500"></div></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="font-semibold text-gray-800">Project Materials</h3>
          <button
            onClick={() => { setSelectedItem(null); setTransferForm({ from_project_id: "", quantity: "", notes: "", item_name: "", unit: "" }); setShowTransferModal(true); }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700 transition shadow-sm">
            <Plus size={14} /> Request Material
          </button>
        </div>
        <button onClick={load} className="flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600 transition"><RefreshCw size={12} /> Refresh</button>
      </div>

      {/* Inventory Status Per Project */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50">
          <p className="font-semibold text-gray-700 text-sm">Material Stock Status</p>
          <p className="text-xs text-gray-400 mt-0.5">Purchased for project + Transfers In − Used − Transfers Out</p>
        </div>
        {inventoryStatus.length === 0 ? (
          <div className="p-6 text-center text-gray-400 text-sm">No materials allocated to this project yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left px-4 py-2">Item</th>
                  <th className="text-right px-4 py-2">Purchased</th>
                  <th className="text-right px-4 py-2">Transferred In</th>
                  <th className="text-right px-4 py-2">Used</th>
                  <th className="text-right px-4 py-2">Transferred Out</th>
                  <th className="text-right px-4 py-2">Remaining</th>
                  <th className="text-left px-4 py-2">Unit</th>
                  <th className="text-center px-4 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {inventoryStatus.filter(item => {
                  return parseFloat(item.purchased_for_project || 0) !== 0 ||
                    parseFloat(item.transferred_in || 0) !== 0 ||
                    parseFloat(item.used_in_project || 0) !== 0 ||
                    parseFloat(item.transferred_out || 0) !== 0;
                }).map(item => {
                  const purchased = parseFloat(item.purchased_for_project || 0);
                  const tIn = parseFloat(item.transferred_in || 0);
                  const used = parseFloat(item.used_in_project || 0);
                  const tOut = parseFloat(item.transferred_out || 0);
                  const remaining = purchased + tIn - used - tOut;
                  return (
                    <tr key={item.id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-medium text-gray-800">{item.item_name}</td>
                      <td className="px-4 py-2.5 text-right text-blue-700">{purchased.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right text-green-700">{tIn > 0 ? `+${tIn.toLocaleString()}` : "—"}</td>
                      <td className="px-4 py-2.5 text-right text-orange-600">{used.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right text-red-500">{tOut > 0 ? `-${tOut.toLocaleString()}` : "—"}</td>
                      <td className={`px-4 py-2.5 text-right font-bold ${remaining < 0 ? "text-red-600" : "text-gray-800"}`}>{remaining.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-gray-500">{item.unit}</td>
                      <td className="px-4 py-2.5 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            onClick={() => { setSelectedItem(item); setShowUsageModal(true); }}
                            className="px-2 py-1 bg-blue-50 text-blue-600 border border-blue-200 rounded text-xs font-semibold hover:bg-blue-600 hover:text-white transition">
                            Log to Usage
                          </button>
                          <button
                            onClick={() => { setSelectedItem(item); setShowTransferModal(true); }}
                            className="px-2 py-1 bg-purple-50 text-purple-600 border border-purple-200 rounded text-xs font-semibold hover:bg-purple-600 hover:text-white transition flex items-center gap-1">
                            <Truck size={10} /> Transfer Stock
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Transfer Stock */}
      {transfers.length > 0 && (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => setShowTransferLog(v => !v)}
              className="flex items-center gap-2 font-semibold text-gray-700 text-sm hover:text-blue-600 transition"
            >
              <ChevronDown size={16} className={`text-gray-400 transition-transform ${showTransferLog ? "" : "-rotate-90"}`} />
              Transfer Stock
              <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{transfers.length}</span>
            </button>
            <div className="flex items-center gap-1.5">
              {[
                { key: "all", label: "All" },
                { key: "accepted", label: "Accept" },
                { key: "rejected", label: "Reject" },
                { key: "pending", label: "Pending" },
              ].map(f => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setTransferFilter(f.key)}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider border transition ${
                    transferFilter === f.key
                      ? f.key === "accepted" ? "bg-emerald-500 text-white border-emerald-500"
                        : f.key === "rejected" ? "bg-red-500 text-white border-red-500"
                        : f.key === "pending" ? "bg-amber-500 text-white border-amber-500"
                        : "bg-blue-600 text-white border-blue-600"
                      : f.key === "accepted" ? "bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-100"
                        : f.key === "rejected" ? "bg-red-50 text-red-600 border-red-100 hover:bg-red-100"
                        : f.key === "pending" ? "bg-amber-50 text-amber-600 border-amber-100 hover:bg-amber-100"
                        : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          {showTransferLog && (
            <div className="divide-y">
              {transfers.filter(t => transferFilter === "all" || t.status === transferFilter).length === 0 ? (
                <div className="p-6 text-center text-gray-400 text-sm">No transfers match this filter.</div>
              ) : transfers.filter(t => transferFilter === "all" || t.status === transferFilter).map(t => {
                const isFrom = String(t.from_project_id) === String(projectId);
                return (
                  <div key={t.id} className="px-4 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-1.5 py-0.5 rounded font-bold ${isFrom ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
                          {isFrom ? "OUT" : "IN"}
                        </span>
                        <span className="text-sm font-medium text-gray-800">{t.item_name} × {parseFloat(t.quantity).toLocaleString()} {t.unit}</span>
                      </div>
                      <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-500">
                        <span className="font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">{t.from_project_name || "Godown"}</span>
                        <ArrowRight size={10} className="text-gray-400" />
                        <span className="font-bold text-gray-700">{t.to_project_name}</span>
                        <span className="text-gray-400 font-normal">· {fmtDate(t.requested_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {t.status === "pending" && isFrom && t.from_project_id && Number(t.from_project_id) !== 0 && (
                        <div className="flex gap-1.5 mr-2">
                          <button
                            disabled={saving}
                            onClick={() => handleTransferStatusUpdate(t.id, "accepted")}
                            className="px-2 py-1 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded text-[10px] font-bold uppercase hover:bg-emerald-600 hover:text-white transition shadow-sm">
                            Accept
                          </button>
                          <button
                            disabled={saving}
                            onClick={() => handleTransferStatusUpdate(t.id, "rejected")}
                            className="px-2 py-1 bg-red-50 text-red-600 border border-red-100 rounded text-[10px] font-bold uppercase hover:bg-red-600 hover:text-white transition shadow-sm">
                            Reject
                          </button>
                        </div>
                      )}
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider shrink-0 shadow-sm border ${t.status === "accepted" ? "bg-green-50 text-green-700 border-green-100" : t.status === "rejected" ? "bg-red-50 text-red-700 border-red-100" : "bg-yellow-50 text-yellow-700 border-yellow-100"}`}>
                        {t.status}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Log to Usage */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <button
          type="button"
          onClick={() => setShowUsageLog(v => !v)}
          className="w-full px-4 py-3 border-b bg-gray-50 flex items-center justify-between gap-2 hover:bg-gray-100/80 transition"
        >
          <div className="flex items-center gap-2 font-semibold text-gray-700 text-sm">
            <ChevronDown size={16} className={`text-gray-400 transition-transform ${showUsageLog ? "" : "-rotate-90"}`} />
            Log to Usage
            <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{usageLog.length}</span>
          </div>
        </button>
        {showUsageLog && (
          usageLog.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">No material usage recorded</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-2">Item</th>
                    <th className="text-right px-4 py-2">Qty Used</th>
                    <th className="text-left px-4 py-2">Unit</th>
                    <th className="text-left px-4 py-2">Date</th>
                    <th className="text-left px-4 py-2">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {usageLog.map(u => (
                    <tr key={u.id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium text-gray-800">{u.item_name}</td>
                      <td className="px-4 py-2 text-right text-orange-600 font-semibold">{parseFloat(u.quantity_used).toLocaleString()}</td>
                      <td className="px-4 py-2 text-gray-500">{u.unit}</td>
                      <td className="px-4 py-2 text-gray-500">{fmtDate(u.usage_date)}</td>
                      <td className="px-4 py-2 text-gray-400">{u.notes || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {/* Material Usage Modal */}
      {showUsageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between p-5 border-b">
              <div className="min-w-0">
                <h3 className="font-bold text-gray-800">Log to Usage</h3>
                <p className="text-xs text-gray-500 truncate">{selectedItem?.item_name}</p>
              </div>
              <button onClick={() => setShowUsageModal(false)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
            </div>
            <form onSubmit={handleUsageSave} className="p-5 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Quantity Used ({selectedItem?.unit}) *</label>
                <input required type="number" step="0.01" min="0.01" value={usageForm.quantity_used} onChange={e => setUsageForm(p => ({ ...p, quantity_used: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-200" placeholder="e.g. 10" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Date *</label>
                <input required type="date" value={usageForm.usage_date} onChange={e => setUsageForm(p => ({ ...p, usage_date: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-200" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                <textarea value={usageForm.notes} onChange={e => setUsageForm(p => ({ ...p, notes: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-200 h-20 resize-none" placeholder="Purpose of usage..."></textarea>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowUsageModal(false)} className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-xl text-sm font-medium hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 bg-blue-600 text-white py-2 rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-60">{saving ? "Saving..." : "Log Usage"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Request Material Transfer Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between p-5 border-b">
              <div className="min-w-0">
                <h3 className="font-bold text-gray-800">{selectedItem ? "Request Transfer" : "Request New Material"}</h3>
                {selectedItem && <p className="text-xs text-gray-500 truncate">{selectedItem.item_name}</p>}
              </div>
              <button onClick={() => setShowTransferModal(false)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
            </div>
            <form onSubmit={handleTransferSave} className="p-5 space-y-3">
              {!selectedItem && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Material Item *</label>
                    <select required value={transferForm.item_name} onChange={e => {
                      const item = inventory.find(i => i.item_name === e.target.value);
                      setTransferForm(p => ({ ...p, item_name: e.target.value, unit: item?.unit || "" }));
                    }} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-200">
                      <option value="">Select material from inventory</option>
                      {inventory.map(i => (
                        <option key={i.id} value={i.item_name}>{i.item_name} ({i.unit})</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Source (From Project) *</label>
                <select required value={transferForm.from_project_id} onChange={e => setTransferForm(p => ({ ...p, from_project_id: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-200">
                  <option value="">Select source project</option>
                  {/* Filter Godown (project_id 0) */}
                  {(!itemAvailability.length || itemAvailability.find(a => a.project_id === 0)?.available_stock > 0) && (
                    <option value="0">Godown / Master Inventory</option>
                  )}
                  {/* Filter other projects */}
                  {projects.filter(p => {
                    if (String(p.id) === String(projectId)) return false;
                    if (!itemAvailability.length) return true;
                    const availability = itemAvailability.find(a => String(a.project_id) === String(p.id));
                    return availability ? availability.available_stock > 0 : false;
                  }).map(p => (
                    <option key={p.id} value={p.id}>{p.project_name}</option>
                  ))}
                </select>
                {transferForm.from_project_id !== "" && (
                  <p className="mt-1.5 text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded inline-block">
                    Available: {itemAvailability.find(a => String(a.project_id) === String(transferForm.from_project_id))?.available_stock || 0} {selectedItem?.unit || transferForm.unit}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Quantity ({selectedItem?.unit || transferForm.unit || "unit"}) *</label>
                <input required type="number" step="0.01" min="0.01" value={transferForm.quantity} onChange={e => setTransferForm(p => ({ ...p, quantity: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-200" placeholder="e.g. 50" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                <textarea value={transferForm.notes} onChange={e => setTransferForm(p => ({ ...p, notes: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-200 h-20 resize-none" placeholder="Reason for transfer..."></textarea>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowTransferModal(false)} className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-xl text-sm font-medium hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 bg-purple-600 text-white py-2 rounded-xl text-sm font-medium hover:bg-purple-700 disabled:opacity-60">{saving ? "Submitting..." : "Submit Request"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main ERPTrackingTab ──────────────────────────────────────────────────────
const ERPTrackingTab = ({ projectId }) => {
  const { user } = useAuthStore();
  const isIncharge = user?.role === "incharge";
  const [activeSubTab, setActiveSubTab] = useState(isIncharge ? "attendance" : "finance");
  const builderId = user?.id;

  const renderSubTab = () => {
    switch (activeSubTab) {
      case "finance": return <FinanceSubTab projectId={projectId} builderId={builderId} />;
      case "attendance": return <AttendanceSubTab projectId={projectId} builderId={builderId} />;
      case "pettycash": return <PettyCashSubTab projectId={projectId} builderId={builderId} />;
      case "materials": return <MaterialsSubTab projectId={projectId} builderId={builderId} />;
      default: return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Sub-tab Navigation */}
      <div className="bg-gray-50/50 p-1 rounded-xl border border-gray-100 flex gap-2 overflow-x-auto">
        {SUB_TABS.filter(tab => !(isIncharge && tab.key === "finance")).map(tab => {
          const Icon = tab.icon;
          const isActive = activeSubTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveSubTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-all flex-shrink-0 ${
                isActive 
                  ? "bg-blue-100/50 text-blue-600 shadow-sm border border-blue-100" 
                  : "text-gray-500 hover:bg-gray-100 hover:text-gray-700 border border-transparent"
              }`}>
              <Icon size={16} className={isActive ? "text-blue-600" : "text-gray-400"} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Sub-tab Content */}
      {renderSubTab()}
    </div>
  );
};

export { FinanceSubTab, AttendanceSubTab, PettyCashSubTab, MaterialsSubTab };
export default ERPTrackingTab;
